import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ResourceCard from '@/components/feature/ResourceCard';
import { categories, resources, siteInfo } from '@/mocks/resources';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [activeTag, setActiveTag] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<string>('latest');

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);

  const filteredResources = useMemo(() => {
    let filtered = resources;
    if (slug && slug !== 'all') {
      filtered = filtered.filter((r) => r.categorySlug === slug);
    }
    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (activeTag !== '全部') {
      filtered = filtered.filter((r) => r.tags.includes(activeTag));
    }
    if (sortBy === 'downloads') {
      filtered = [...filtered].sort((a, b) => b.downloadCount - a.downloadCount);
    } else if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    }
    return filtered;
  }, [slug, query, activeTag, sortBy]);

  const allTags = useMemo(() => {
    const resourcePool = slug && slug !== 'all' ? resources.filter((r) => r.categorySlug === slug) : resources;
    const tagSet = new Set<string>();
    resourcePool.forEach((r) => r.tags.forEach((t) => tagSet.add(t)));
    return ['全部', ...Array.from(tagSet)];
  }, [slug]);

  useEffect(() => {
    setActiveTag('全部');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const pageTitle = category ? `${category.name} - ${siteInfo.name}` : `全部资源 - ${siteInfo.name}`;
  const pageDesc = category
    ? `${category.description} - ${siteInfo.name}`
    : `${siteInfo.name}全部资源分类，涵盖学习、办公、设计、工具等多个领域的精品资源合集。`;

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`${category?.name || '资源'}下载,${siteInfo.name},免费${category?.name || '资源'},优质${category?.name || '资源'}合集`}
        canonicalUrl={`https://geiliziyuan.com/category/${slug}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: pageDesc,
          url: `https://geiliziyuan.com/category/${slug}`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/60">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  ...(category ? [{ label: category.name }] : [{ label: '全部资源' }]),
                ]}
              />
              <div className="px-4 md:px-6 pb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  {category ? category.name : '全部资源'}
                </h1>
                <p className="text-sm text-foreground-500">
                  {category ? category.description : '浏览所有分类的精品资源'} {query && `- 搜索："${query}"`}
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <aside className="lg:w-56 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h3 className="font-heading text-sm font-bold text-foreground-800 mb-3">资源分类</h3>
                  <nav className="space-y-1 mb-6">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`block px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          slug === cat.slug
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-primary-600'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={`${cat.icon} text-base`}></i>
                          {cat.name}
                        </span>
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-2 flex-wrap">
                    {allTags.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => setActiveTag(tag)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          activeTag === tag
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-1.5 rounded-lg bg-background-100 text-sm text-foreground-700 border border-background-200 outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="latest">最新发布</option>
                      <option value="downloads">下载最多</option>
                      <option value="rating">评分最高</option>
                    </select>
                  </div>
                </div>

                {filteredResources.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
                      <i className="ri-inbox-line text-3xl"></i>
                    </div>
                    <p className="text-sm text-foreground-500">暂无匹配的资源</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {filteredResources.map((resource) => (
                      <ResourceCard key={resource.id} resource={resource} />
                    ))}
                  </div>
                )}

                {filteredResources.length > 0 && (
                  <div className="mt-10 text-center text-sm text-foreground-400">
                    共 {filteredResources.length} 份资源
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}